import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarCheck, Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const scopeLabel = (scope) => {
  if (scope === 'centre') return 'Centre';
  if (scope?.startsWith('tuteur:')) return 'Tuteur';
  if (scope?.startsWith('entreprise:')) return 'Entreprise';
  return scope || '—';
};

const formatDate = (d) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
};

export const FeuillesDuJourCard = () => {
  const [feuilles, setFeuilles] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    axios.get('/feuilles-presence/jour')
      .then(({ data }) => setFeuilles(data))
      .catch(() => setFeuilles([]));
  }, []);

  const telecharger = async (f) => {
    setDownloading(f.feuille_id);
    try {
      const res = await axios.get(`/feuilles-presence/jour/${f.feuille_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Feuille_du_jour_${f.date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Impossible de télécharger cette feuille du jour');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="feuilles-du-jour-card">
      <h2 className="text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2 mb-1">
        <CalendarCheck className="h-5 w-5 text-[#8DC63F]" />
        Feuilles du jour conservées
      </h2>
      <p className="text-sm text-slate-500 mb-4">Chaque feuille du jour envoyée à la direction est enregistrée ici. Elles sont aussi ajoutées à l'archive de fin de formation.</p>
      {feuilles === null ? (
        <div className="flex items-center justify-center py-6 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>
      ) : feuilles.length === 0 ? (
        <p className="text-center text-slate-500 py-6" data-testid="feuilles-du-jour-empty">Aucune feuille du jour conservée pour le moment</p>
      ) : (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm" data-testid="feuilles-du-jour-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Jour</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Périmètre</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Stagiaires</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Origine</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {feuilles.map((f) => (
                <tr key={f.feuille_id} className="hover:bg-slate-50" data-testid={`feuille-jour-row-${f.date}-${f.scope}`}>
                  <td className="px-4 py-3 font-medium">{formatDate(f.date)}</td>
                  <td className="px-4 py-3">{scopeLabel(f.scope)}</td>
                  <td className="px-4 py-3">{f.nb_stagiaires}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {f.regenere ? 'Recréée depuis les pointages' : `Envoyée${f.envoye_par_nom ? ` par ${f.envoye_par_nom}` : ''}`}
                  </td>
                  <td className="px-4 py-3">
                    <Button variant="ghost" size="sm" onClick={() => telecharger(f)} disabled={downloading === f.feuille_id} className="text-[#8DC63F] hover:text-[#7db336] hover:bg-green-50" data-testid={`btn-download-feuille-jour-${f.feuille_id}`}>
                      {downloading === f.feuille_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeuillesDuJourCard;
